import { useMutation } from '@tanstack/react-query'
import { ActionBar, CancelIcon, cn, Dialog } from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { type Column, GenericTable } from '../../../../components/GenericTable'
import { pluralize } from '../../../../utils/formatters'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc } from '../../../../utils/trpc'
import type { CycleEmailGlobalExclusion, CycleEmailGlobalExclusionSearchInput } from '../types'
import { CycleAddGlobalExclusionsModal } from './CycleAddGlobalExclusionsModal'

const columns: Column<CycleEmailGlobalExclusion>[] = [
	{
		field: 'firstName',
		label: 'Name',
		sortable: true,
		render: (exclusion: CycleEmailGlobalExclusion) => `${exclusion.firstName} ${exclusion.lastName}`,
	},
	{
		field: 'email',
		label: 'Email',
		sortable: true,
	},
]

export function CycleEmailGlobalExclusionsTab() {
	const { cycleId } = useParams<{ cycleId: string }>()
	const [selectedExclusions, setSelectedExclusions] = useState<CycleEmailGlobalExclusion[]>([])
	const [isAddGlobalExclusionsModalOpen, setIsAddGlobalExclusionsModalOpen] = useState(false)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const toast = useToast()
	const { mutate: deleteCycleEmailsGlobalExclusion, isPending: isDeleting } = useMutation(
		trpc.admin.cycles.emails.globalExclusions.delete.mutationOptions({
			onSuccess: () => {
				toast.showToast('Exclusions deleted', 'info')
				setSelectedExclusions([])
				setIsDeleteDialogOpen(false)
			},
			onError: (error) => {
				toast.showToast(error.message ?? 'Failed to delete exclusions', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.emails.globalExclusions.search.queryKey(),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)
	return (
		<>
			<div className={cn({ 'mb-24': selectedExclusions.length > 0 })}>
				<GenericTable<CycleEmailGlobalExclusion, CycleEmailGlobalExclusionSearchInput>
					queryOptionsGenerator={(options) => ({
						...trpc.admin.cycles.emails.globalExclusions.search.queryOptions({
							...options,
							cycleId: cycleId || '',
						}),
						enabled: !!cycleId,
					})}
					isInternal
					columns={columns}
					showPagination
					selectable
					onSelectionChange={setSelectedExclusions}
					actionButtons={[
						{
							children: '+ Add Exclusion',
							variant: 'secondary',
							onClick: () => setIsAddGlobalExclusionsModalOpen(true),
						},
					]}
					emptyState={{
						title: 'No exclusions created',
						description: 'Get started by adding an exclusion',
						icon: CancelIcon,
						actionLabel: 'Add Exclusion',
						onAction: () => {
							setIsAddGlobalExclusionsModalOpen(true)
						},
					}}
				/>
			</div>

			<CycleAddGlobalExclusionsModal
				isOpen={isAddGlobalExclusionsModalOpen}
				onClose={() => setIsAddGlobalExclusionsModalOpen(false)}
				cycleId={cycleId as string}
			/>

			{selectedExclusions.length > 0 && (
				<ActionBar
					rightActions={[
						{
							label: 'Cancel',
							variant: 'secondary',
							onClick: () => setSelectedExclusions([]),
						},
						{
							label: `Delete${selectedExclusions.length > 0 ? ` (${selectedExclusions.length})` : ''}`,
							variant: 'destructive',
							onClick: () => setIsDeleteDialogOpen(true),
						},
					]}
				/>
			)}

			<Dialog
				isOpen={isDeleteDialogOpen}
				onClose={() => {
					if (isDeleting) return
					setIsDeleteDialogOpen(false)
				}}
				onConfirm={() => {
					deleteCycleEmailsGlobalExclusion({
						cycleId: cycleId as string,
						userIds: selectedExclusions.map((exclusion) => exclusion.userId),
					})
				}}
				isConfirmLoading={isDeleting}
				title='Delete cycle participants'
				message={`Are you sure you want to delete ${selectedExclusions.length} global ${pluralize(selectedExclusions.length, 'exclusion', 'exclusions')}?`}
				type='delete'
				confirmLabel='Delete'
			/>
		</>
	)
}
